import * as React from 'react';
import { compose, withProps, withHandlers, withState, lifecycle } from 'recompose';
import { reduxForm, FormSection, change as reduxChange, getFormValues } from 'redux-form';
import { format } from 'date-fns';
import * as uuid from 'uuid/v4';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { Grid, Row, Col } from 'react-flexbox-grid';
import Divider from 'material-ui/Divider';
import Button from '@inqool/uas-web-core/es/components/controls/Button';
import { required } from '@utils/validators';

import { Person } from '@components/person';
import {
  Input,
  InputField,
  Toggle,
  ToggleField,
  Date as DateForm,
  DateField,
  AutoComplete,
  AutoCompleteField,
  RemoteAutoComplete,
  RemoteAutoCompleteField,
  Photo,
  PhotoField
} from '@components/form';
import Address, { AddressField } from '@components/form/Address/Address';
import { sexEnum, stateEnum } from '@modules/dogs/_dogsEnums';
import { Loader } from '@components/loader';
import { putDog } from '@modules/dogs/_dogsActions';
import { withNotifications } from '@components/hoc/withNotifications';
import { participantsTypeEnum } from './_eventsEnums';

const formName = 'module-events-dogs';

const mapStateToProps = state => ({
  dogs: get(state, 'form.module-events.values.dogs', []),
  participants: get(state, 'form.module-events.values.participants', []),
  values: getFormValues(formName)(state)
});

const mapDispatchToProps = dispatch => ({
  setDogs: dogs => dispatch(reduxChange('module-events', 'dogs', dogs)),
  setParticipants: participants => dispatch(reduxChange('module-events', 'participants', participants)),
  setCreateDog: createDog => dispatch(reduxChange(formName, 'createDog', createDog))
});

const enhance = compose(
  reduxForm({
    form: formName,
    keepDirtyOnReinitialize: true,
    enableReinitialize: true
  }),
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withNotifications({ putDog }),
  withProps(() => {
    return { maxYear: format(new Date(), 'YYYY') };
  }),
  withState<{}, boolean, string, string>('loader', 'handleLoader', false),
  withState<{}, boolean, string, string>('disabledCreate', 'setDisabledCreate', true),
  withHandlers({
    onSubmit: ({
      handleLoader,
      setDogs,
      dogs,
      closeItem,
      putDogNotify,
      initialValues,
      participants,
      setParticipants
    }) => async formData => {
      handleLoader(true);
      if (formData.addAsParticipant) {
        const participantCustom = [
          ...participants.filter(p => get(p, 'person.id') !== get(formData, 'owner.id')),
          { person: formData.owner, type: formData.participantType }
        ];
        const participantSelector = [
          ...participants.filter(p => get(p, 'person.id') !== get(formData, 'dogsSelector.owner.id')),
          { person: get(formData, 'dogsSelector.owner'), type: formData.participantType }
        ];
        if (formData.createDog) {
          setParticipants(participantCustom);
        } else {
          setParticipants(participantSelector);
        }
      }
      if (formData.dogsSelector && !formData.createDog) {
        setDogs([...dogs.filter(d => d.id !== formData.dogsSelector.id), formData.dogsSelector]);
      } else {
        const body = { id: get(formData, 'id', uuid()), ...formData };
        const result = await putDogNotify(body);
        if (result) {
          if (initialValues) {
            setDogs([...dogs.filter(d => d.id !== result.id), result]);
          } else {
            setDogs([...dogs, result]);
          }
        }
      }
      closeItem();
      handleLoader(false);
    }
  }),
  lifecycle<any, any>({
    componentDidMount() {
      const { initialValues, setCreateDog } = this.props;
      if (initialValues) {
        setCreateDog(true);
      }
    }
  })
);

const View: React.SFC<any> = ({
  handleSubmit,
  closeItem,
  maxYear,
  loader,
  onSubmit,
  change,
  values,
  initialValues,
  disabledCreate,
  setDisabledCreate
}) => {
  const createDog = get(values, 'createDog');
  const addAsParticipant = get(values, 'addAsParticipant');
  React.useEffect(
    () => {
      if (createDog) {
        setDisabledCreate(false);
        change('dogsSelector', null);
      } else {
        setDisabledCreate(true);
      }
    },
    [createDog]
  );
  return (
    <div>
      {loader && <Loader />}
      <form className="layout" onSubmit={handleSubmit}>
        <Grid fluid={true}>
          <Grid fluid={true}>
            <Row>
              <Col className="grid-system-margin" />
            </Row>
            <Row style={{ alignItems: 'center' }}>
              <Col md={6}>
                <RemoteAutoCompleteField
                  name="dogsSelector"
                  url="/metropolis/api/dg/dogs"
                  component={RemoteAutoComplete}
                  fullWidth={true}
                  label={disabledCreate ? 'Vyberte psa (číslo čipu) *' : 'Vyberte psa (číslo čipu)'}
                  labelFunction={o => `${get(o, 'name')} (${get(o, 'chipNumber', '-')})`}
                  filterActive={false}
                  validate={disabledCreate ? required : undefined}
                  disabled={!disabledCreate}
                />
              </Col>

              <Col md={6}>
                <ToggleField
                  component={Toggle}
                  name="createDog"
                  label={initialValues ? `Editovat psa` : `Vytvořit nového psa`}
                  labelPosition="right"
                  formChange={change}
                />
              </Col>
            </Row>
            <Row style={{ margin: '10px 0', alignItems: 'center' }}>
              <Col md={6}>
                <ToggleField
                  component={Toggle}
                  name="addAsParticipant"
                  label="Přidat majitele psa jako účastníka"
                  labelPosition="right"
                  formChange={change}
                />
              </Col>
              {addAsParticipant && (
                <Col md={6}>
                  <AutoCompleteField
                    component={AutoComplete}
                    name="participantType"
                    fullWidth={true}
                    label="Typ účastníka *"
                    labelFunction={o => get(o, 'label')}
                    valueFunction={o => get(o, 'id')}
                    items={participantsTypeEnum}
                    validate={required}
                  />
                </Col>
              )}
            </Row>
            <Row>
              <Col>
                <h4>{initialValues ? `Editovat psa` : `Vytvořit nového psa`}</h4>
              </Col>
            </Row>
            <Row>
              <Col md={4} xl={2} className="with-right-divider">
                <Row>
                  <Col md={12}>
                    <h4>Fotografie</h4>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <PhotoField component={Photo} name="photo" disabled={disabledCreate} />
                  </Col>
                </Row>
              </Col>
              <Col md={4} xl={2} className="with-right-divider">
                <Row>
                  <Col md={12}>
                    <h4>Základní údaje</h4>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <InputField component={Input} name="name" label="Jméno" disabled={disabledCreate} />
                  </Col>
                </Row>
                {[
                  { name: 'sex', label: 'Pohlaví', items: sexEnum },
                  { name: 'state', label: 'Stav registrace', items: stateEnum }
                ].map(field => (
                  <Row key={field.name}>
                    <Col md={12}>
                      <AutoCompleteField
                        component={AutoComplete}
                        {...field}
                        fullWidth={true}
                        labelFunction={o => get(o, 'name')}
                        valueFunction={o => get(o, 'id')}
                        disabled={disabledCreate}
                      />
                    </Col>
                  </Row>
                ))}
                {[
                  { name: 'breed', label: 'Plemeno', url: 'api/dg/breeds', labelProp: 'name' },
                  { name: 'color', label: 'Barva', url: 'api/dg/colors', labelProp: 'name' }
                ].map(({ labelProp, ...props }) => (
                  <Row key={props.name}>
                    <Col md={12}>
                      <RemoteAutoCompleteField
                        component={RemoteAutoComplete}
                        fullWidth={true}
                        labelFunction={o => get(o, labelProp)}
                        filterActive={true}
                        {...props}
                        disabled={disabledCreate}
                      />
                    </Col>
                  </Row>
                ))}
                <Row>
                  <Col md={12}>
                    <h4>Majitel</h4>
                  </Col>
                </Row>

                <FormSection name="owner">
                  <Person form={formName} formName="owner." disabled={disabledCreate} />
                </FormSection>
                <AddressField component={Address} name={'owner.address'} disabled={disabledCreate} />
              </Col>

              <Col md={4} xl={2} className="with-right-divider">
                <Row>
                  <Col md={12}>
                    <h4>Identifikace</h4>
                  </Col>
                </Row>
                {[
                  { name: 'chipNumber', label: 'Číslo čipu' },
                  { name: 'chipNumberExternal', label: 'Číslo čipu - externí' },
                  { name: 'tattoo', label: 'Číslo tetování' },
                  { name: 'idNumber', label: 'Číslo známky' }
                ].map(field => (
                  <Row key={field.name}>
                    <Col md={12}>
                      <InputField component={Input} {...field} disabled={disabledCreate} />
                    </Col>
                  </Row>
                ))}
                <Row>
                  <Col md={12}>
                    <div style={{ marginTop: 15 }}>
                      <ToggleField
                        component={Toggle}
                        name="idLost"
                        label="Známka ztracena"
                        labelPosition="right"
                        formChange={change}
                        disabled={disabledCreate}
                      />
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col md={8} xl={4} className="with-right-divider">
                <Row>
                  <Col md={12}>
                    <h4>Pokročilé informace</h4>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    {[
                      { name: 'birthDate', label: 'Datum narození' },
                      { name: 'vaccinationDate', label: 'Datum vakcinace' },
                      { name: 'idDate', label: 'Datum vydání známky' },
                      { name: 'tattooDate', label: 'Datum tetování' },
                      { name: 'chipDate', label: 'Datum čipování' }
                    ].map(field => (
                      <Row key={field.name}>
                        <Col md={12}>
                          <DateField component={DateForm} {...field} disabled={disabledCreate} />
                        </Col>
                      </Row>
                    ))}
                  </Col>
                  <Col md={6}>
                    {[
                      { name: 'externalId', label: 'Identifikátor v externím systému' },
                      {
                        name: 'yearFee',
                        label: 'Rok zaplacení poplatku',
                        type: 'number',
                        min: 1900,
                        max: maxYear
                      }
                    ].map(field => (
                      <Row key={field.name}>
                        <Col md={12}>
                          <InputField component={Input} {...field} disabled={disabledCreate} />
                        </Col>
                      </Row>
                    ))}
                  </Col>
                </Row>
              </Col>
              <Col md={12} xl={2}>
                <Row>
                  <Col md={12}>
                    <h4>Doplňující informace</h4>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <InputField
                      component={Input}
                      multiLine={true}
                      name="note"
                      label="Poznámka"
                      style={{ maxHeight: '60vh' }}
                      inputStyle={{ maxHeight: '60vh' }}
                      textareaStyle={{ maxHeight: '56vh' }}
                      disabled={disabledCreate}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Grid>

          <Row>
            <Col className="grid-system-margin" />
          </Row>
        </Grid>

        <Divider />

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            onClick={handleSubmit(onSubmit)}
            label="Uložit a zavřít"
            primary={true}
            style={{ margin: '16px 0 16px 16px' }}
          />
          <Button label="Zrušit" style={{ margin: '16px 0 16px 16px' }} onClick={closeItem} />
        </div>
      </form>
    </div>
  );
};

export const EventsFormParticipantsDogsForm = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/events/EventsFormParticipantsDogsForm.tsx